import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Chip,
  Button,
  IconButton,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  Divider,
  Alert,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import PersonPinCircleOutlinedIcon from "@mui/icons-material/PersonPinCircleOutlined";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import { useNavigate } from "react-router-dom";
import { CashkaroService } from "../../../core/services";
import { CashkaroLink } from "./types";
import { CC, categoryColor, PayoutChip, CampaignMetaRow, CampaignTermsHeader, termsListSx } from "./CashkaroLayout";

// tracking_window_minutes is always in minutes (see campaignNormalizer.ts).
function formatDuration(minutes: number | null | undefined): string | null {
  if (!minutes && minutes !== 0) return null;
  if (minutes % 1440 === 0) return `${minutes / 1440} day${minutes / 1440 === 1 ? "" : "s"}`;
  if (minutes % 60 === 0) return `${minutes / 60} hour${minutes / 60 === 1 ? "" : "s"}`;
  return `${minutes} min`;
}

// A link's own tracking_url is a long redirect string (cashkaro.gotrackier.com/click?...)
// — never fit to show as the primary line. Prefer the influencer's own label,
// then fall back to the destination they pointed it at (host + path), which
// reads as "what this link is for" instead of raw redirect plumbing.
function friendlyLinkLabel(link: CashkaroLink): string {
  if (link.label) return link.label;
  try {
    const url = new URL(link.target_url);
    const path = url.pathname !== "/" ? url.pathname : "";
    return `${url.hostname.replace(/^www\./, "")}${path}`;
  } catch {
    return link.target_url || (link.click_token ? CashkaroService.clickUrl(link.click_token) : link.tracking_url);
  }
}

interface Props {
  campaignId: number | null;
  influencerId: number;
  accountId?: number;
  open: boolean;
  onClose: () => void;
}

// Two things at once, per request: (1) full campaign detail before/without
// requiring the influencer to leave the grid, and (2) for each link already
// generated on this campaign, whether it's already posted to Storefront /
// Link in Bio — reusing the same addLinkToStorefront/addLinkToBio cross-post
// actions already built for the My Links page.
export const CashkaroCampaignPreviewDialog: React.FC<Props> = ({ campaignId, influencerId, accountId, open, onClose }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [normalized, setNormalized] = useState<any>(null);
  const [cached, setCached] = useState<any>(null);
  const [accessStatus, setAccessStatus] = useState("none");
  const [links, setLinks] = useState<CashkaroLink[]>([]);
  const [linksLoading, setLinksLoading] = useState(false);
  const [copiedId, setCopiedId] = useState<number | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !campaignId || !influencerId) return;
    setLoading(true);
    setActionError(null);
    CashkaroService.getCampaignDetail(campaignId, influencerId)
      .then((res) => {
        const data = res?.data || {};
        setNormalized(data.normalized || null);
        setCached(data.cached || null);
        setAccessStatus(data.access_status || "none");
      })
      .finally(() => setLoading(false));
  }, [open, campaignId, influencerId]);

  useEffect(() => {
    if (!open || !campaignId || !influencerId) {
      setLinks([]);
      return;
    }
    setLinksLoading(true);
    CashkaroService.getMyLinks(influencerId, campaignId)
      .then((res) => {
        const list = res?.data?.links;
        setLinks(Array.isArray(list) ? list : []);
      })
      .catch(() => setLinks([]))
      .finally(() => setLinksLoading(false));
  }, [open, campaignId, influencerId]);

  // Our own redirect domain, never the raw Trackier tracking_url (it embeds
  // our shared pub_id and internal cctm/product params in plaintext) — falls
  // back to tracking_url only for legacy rows saved before click_token existed.
  const displayUrl = (link: CashkaroLink) => (link.click_token ? CashkaroService.clickUrl(link.click_token) : link.tracking_url);

  const handleCopy = (link: CashkaroLink) => {
    navigator.clipboard.writeText(displayUrl(link));
    setCopiedId(link.id);
    setTimeout(() => setCopiedId(null), 1500);
  };

  const handleAddToStorefront = async (link: CashkaroLink) => {
    if (!accountId) return;
    setActionError(null);
    setActionLoading(`${link.id}-storefront`);
    try {
      const res = await CashkaroService.addLinkToStorefront({ link_id: link.id, influencer_id: influencerId, account_id: accountId });
      if (res?.data?.requires_setup) {
        setActionError(res.data.message || "Set up your Zap Storefront first");
      } else {
        setLinks((prev) => prev.map((l) => (l.id === link.id ? { ...l, on_storefront: true } : l)));
      }
    } catch {
      setActionError("Could not add to Storefront right now — please try again.");
    } finally {
      setActionLoading(null);
    }
  };

  const handleAddToBio = async (link: CashkaroLink) => {
    if (!accountId) return;
    setActionError(null);
    setActionLoading(`${link.id}-bio`);
    try {
      const res = await CashkaroService.addLinkToBio({ link_id: link.id, influencer_id: influencerId, account_id: accountId });
      if (res?.data?.requires_setup) {
        setActionError(res.data.message || "Set up your Zap Link in Bio page first");
      } else {
        setLinks((prev) => prev.map((l) => (l.id === link.id ? { ...l, on_bio: true } : l)));
      }
    } catch {
      setActionError("Could not add to Bio right now — please try again.");
    } finally {
      setActionLoading(null);
    }
  };

  const title = normalized?.title || cached?.title;
  const description = normalized?.description;
  const termsHtml = normalized?.terms_html;
  const thumbnail = normalized?.thumbnail || cached?.thumbnail;
  const rawCategories = normalized?.categories?.length ? normalized.categories : cached?.categories;
  const categories: string[] = Array.isArray(rawCategories) ? rawCategories : [];
  const trackingWindow = formatDuration(normalized?.tracking_window_minutes);
  const validationDays = normalized?.validation_window_days;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: CC.radiusLg, fontFamily: CC.font, boxShadow: "0 24px 48px rgba(15,23,42,.16)" } }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 3,
          pt: 2.5,
          pb: 2,
          borderBottom: `1px solid ${CC.border}`,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: CC.radiusSm,
              bgcolor: CC.primaryWash,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <CampaignOutlinedIcon sx={{ fontSize: 18, color: CC.primary }} />
          </Box>
          <Typography variant="subtitle1" fontWeight={700} sx={{ color: CC.textPrimary, fontFamily: CC.font }}>
            Campaign details
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose} sx={{ color: CC.textSecondary, "&:hover": { bgcolor: CC.bg } }}>
          <CloseRoundedIcon fontSize="small" />
        </IconButton>
      </Box>

      <DialogContent sx={{ pt: 2 }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress size={26} sx={{ color: CC.primary }} />
          </Box>
        ) : (
          <>
            <Box sx={{ display: "flex", gap: 1.75, alignItems: "flex-start" }}>
              <Box
                sx={{
                  width: 60,
                  height: 60,
                  borderRadius: CC.radiusMd,
                  bgcolor: "#fff",
                  border: `1px solid ${CC.border}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  overflow: "hidden",
                }}
              >
                {thumbnail ? (
                  <Box sx={{ width: "100%", height: "100%", p: 0.75, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <Box component="img" src={thumbnail} alt={title} sx={{ maxWidth: "100%", maxHeight: "100%", width: "auto", height: "auto", objectFit: "contain" }} />
                  </Box>
                ) : (
                  <StorefrontOutlinedIcon sx={{ fontSize: 24, color: "#c7cad1" }} />
                )}
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ color: CC.textPrimary, fontFamily: CC.font }}>
                  {title}
                </Typography>
                <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", my: 0.5 }}>
                  {categories.map((cat) => {
                    const color = categoryColor(cat);
                    return (
                      <Chip
                        key={cat}
                        label={cat}
                        size="small"
                        sx={{
                          textTransform: "capitalize",
                          height: 20,
                          fontSize: 11,
                          fontFamily: CC.font,
                          borderRadius: "999px",
                          bgcolor: `${color}1a`,
                          color,
                        }}
                      />
                    );
                  })}
                  {!normalized?.hide_payout && normalized?.payout_summary && <PayoutChip label={normalized.payout_summary} height={20} />}
                </Box>
                <CampaignMetaRow trackingWindow={trackingWindow} validationDays={validationDays} iconSize={13} />
              </Box>
            </Box>

            {description && (
              <Typography
                variant="body2"
                sx={{ color: CC.textSecondary, fontFamily: CC.font, lineHeight: 1.6, mt: 1.75, maxHeight: 130, marginBottom: 1.25, overflowY: "auto" }}
                dangerouslySetInnerHTML={{ __html: description }}
              />
            )}

            {termsHtml && (
              <Accordion
                disableGutters
                elevation={0}
                square
                sx={{ mt: 1.5, border: `1px solid ${CC.border}`, borderRadius: CC.radiusSm, overflow: "hidden", "&:before": { display: "none" } }}
              >
                <AccordionSummary expandIcon={<ExpandMoreRoundedIcon sx={{ color: CC.textSecondary }} />} sx={{ bgcolor: CC.bg }}>
                  <CampaignTermsHeader />
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body2" sx={{ color: CC.textSecondary, fontFamily: CC.font, lineHeight: 1.6, ...termsListSx }} dangerouslySetInnerHTML={{ __html: termsHtml }} />
                </AccordionDetails>
              </Accordion>
            )}

            <Divider sx={{ my: 2, borderColor: CC.border }} />

            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.25 }}>
              <LinkOutlinedIcon fontSize="small" sx={{ color: CC.primary }} />
              <Typography variant="subtitle2" fontWeight={700} sx={{ color: CC.textPrimary, fontFamily: CC.font }}>
                Your links for this campaign
              </Typography>
            </Box>

            {accessStatus !== "approved" ? (
              <Alert severity={accessStatus === "pending" ? "warning" : "info"} sx={{ borderRadius: CC.radiusMd, fontFamily: CC.font }}>
                {accessStatus === "pending"
                  ? "Your access request is pending — you'll be able to generate links once it's approved."
                  : "Join this campaign to generate a trackable link."}
              </Alert>
            ) : linksLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                <CircularProgress size={20} sx={{ color: CC.primary }} />
              </Box>
            ) : links.length === 0 ? (
              <Box sx={{ py: 1 }}>
                <Typography variant="body2" sx={{ color: CC.textSecondary, fontFamily: CC.font, mb: 1.5 }}>
                  No links generated yet for this campaign.
                </Typography>
                <Button
                  size="small"
                  disableElevation
                  startIcon={<LinkOutlinedIcon fontSize="small" />}
                  onClick={() => {
                    onClose();
                    navigate(`/influencer/brand-deals/campaigns/${campaignId}`);
                  }}
                  sx={{
                    bgcolor: "#000",
                    color: "#fff",
                    textTransform: "none",
                    fontWeight: 600,
                    fontFamily: CC.font,
                    borderRadius: "999px",
                    px: 2,
                    "&:hover": { bgcolor: "#111" },
                  }}
                >
                  Generate a link
                </Button>
              </Box>
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {links.map((link) => (
                  <Box
                    key={link.id}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      p: 1.25,
                      border: `1px solid ${CC.border}`,
                      borderRadius: CC.radiusSm,
                      bgcolor: CC.card,
                      transition: "border-color .15s ease",
                      "&:hover": { borderColor: CC.borderHover },
                    }}
                  >
                    <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        sx={{ color: CC.textPrimary, fontFamily: CC.font, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                        title={link.target_url}
                      >
                        {friendlyLinkLabel(link)}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: CC.textSecondary, fontFamily: CC.font, display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                        title={displayUrl(link)}
                      >
                        {displayUrl(link)}
                      </Typography>
                    </Box>
                    <Tooltip title={copiedId === link.id ? "Copied!" : "Copy link"}>
                      <IconButton size="small" onClick={() => handleCopy(link)} sx={{ color: copiedId === link.id ? "success.main" : CC.textSecondary }}>
                        {copiedId === link.id ? <CheckRoundedIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={link.on_storefront ? "On your Storefront" : "Add to Storefront"}>
                      <span>
                        <IconButton
                          size="small"
                          disabled={!!link.on_storefront || actionLoading === `${link.id}-storefront`}
                          onClick={() => handleAddToStorefront(link)}
                          sx={{ color: link.on_storefront ? "success.main" : CC.textSecondary }}
                        >
                          {actionLoading === `${link.id}-storefront` ? (
                            <CircularProgress size={16} />
                          ) : link.on_storefront ? (
                            <CheckRoundedIcon fontSize="small" />
                          ) : (
                            <StorefrontOutlinedIcon fontSize="small" />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title={link.on_bio ? "On your Bio page" : "Add to Link in Bio"}>
                      <span>
                        <IconButton
                          size="small"
                          disabled={!!link.on_bio || actionLoading === `${link.id}-bio`}
                          onClick={() => handleAddToBio(link)}
                          sx={{ color: link.on_bio ? "success.main" : CC.textSecondary }}
                        >
                          {actionLoading === `${link.id}-bio` ? (
                            <CircularProgress size={16} />
                          ) : link.on_bio ? (
                            <CheckRoundedIcon fontSize="small" />
                          ) : (
                            <PersonPinCircleOutlinedIcon fontSize="small" />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                ))}
              </Box>
            )}

            {actionError && (
              <Alert severity="info" sx={{ mt: 1.5, borderRadius: CC.radiusMd, fontFamily: CC.font }}>
                {actionError}
              </Alert>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, pt: 1,  borderTop: `1px solid ${CC.border}` }}>
        <Button
          onClick={() => {
            onClose();
            navigate(`/influencer/brand-deals/campaigns/${campaignId}`);
          }}
          startIcon={<OpenInNewOutlinedIcon sx={{ fontSize: 16 }} />}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            fontFamily: CC.font,
            color: CC.primary,
            "&:hover": { bgcolor: CC.primaryWash },
          }}
        >
          Open full page
        </Button>
        <Button
          onClick={onClose}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            fontFamily: CC.font,
            color: CC.textPrimary,
            border: `1px solid ${CC.border}`,
            borderRadius: "999px",
            px: 2.5,
            "&:hover": { borderColor: CC.borderHover, bgcolor: CC.bg },
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CashkaroCampaignPreviewDialog;
